// pages/DepositPage.tsx
// ⭐ P2P Deposit (Merchant Portal) — สร้างรายการฝาก → โอนเข้าบัญชีปลายทาง → อัปสลิป
import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import socketService from '../services/socketService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandHoldingDollar, faUniversity, faCopy, faCloudUpload, faCheckCircle, faLink,
  faClock, faTimesCircle, faArrowRightArrowLeft, faBuilding, faPlus,
  faHourglassHalf, faBan, faLock, faSearch, faTimes, faDownload, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiButton, UiInput, UiFilterPanel, StatusBadge } from '../components/ui';
import type { StatusDef } from '../components/ui';
import CountdownTimer from '../components/shared/CountdownTimer';
import DataTable from '../components/shared/DataTable';
import type { Column } from '../components/shared/DataTable';
import { useServerPagination } from '../hooks/useServerPagination';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

interface DepositOrder {
  order_number: string;
  reference_id?: string;
  amount: number;
  fee_amount?: number;
  net_amount?: number;
  status: 'pending' | 'paid' | 'expired' | 'cancelled' | 'expired_locked';
  routed_to_payout: boolean;
  destination_bank_name?: string;
  destination_account_number?: string;
  destination_account_name?: string;
  expires_at: string;
  paid_at?: string;
  created_at: string;
  seconds_left?: number;
  is_expired?: boolean;
}

const STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faHourglassHalf, label: 'รอชำระ' },
  paid: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'สำเร็จ' },
  expired: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faTimesCircle, label: 'หมดอายุ' },
  expired_locked: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faLock, label: 'หมดอายุ (ล็อก)' },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faBan, label: 'ยกเลิก' },
};

const CARD = 'bg-white dark:bg-slate-800 rounded-2xl shadow-theme';

const DepositPage: React.FC = () => {
  const [amount, setAmount] = useState('');
  const [creating, setCreating] = useState(false);
  const [current, setCurrent] = useState<DepositOrder | null>(null);
  const [deposits, setDeposits] = useState<DepositOrder[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tick, setTick] = useState(0);
  const { currentPage, pageSize, setCurrentPage, setTotalItems, serverPaginationProps } = useServerPagination(10);

  // ── ค้นหา (debounce) + กรองวันที่ (default = วันนี้) ──
  const today = dayjs().tz(TZ).format('YYYY-MM-DD');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(today);
  const [appliedDateTo, setAppliedDateTo] = useState(today);

  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const delay = setTimeout(() => { setSearchTerm(searchInput); setCurrentPage(1); }, 400);
    return () => clearTimeout(delay);
  }, [searchInput, setCurrentPage]);

  const loadDeposits = useCallback(async () => {
    try {
      setLoadingList(true);
      const params = new URLSearchParams();
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));
      if (searchTerm) params.append('search', searchTerm);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      const res = await merchantApi.get(`/merchants/deposit-orders?${params.toString()}`);
      if (res.data.success) { setDeposits(res.data.data || []); setTotalItems(res.data.total || 0); }
    } catch { /* ignore */ } finally { setLoadingList(false); }
  }, [currentPage, pageSize, searchTerm, appliedDateFrom, appliedDateTo, setTotalItems]);

  useEffect(() => { loadDeposits(); }, [loadDeposits]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) { toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด'); return; }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setDateFrom(today); setDateTo(today);
    setAppliedDateFrom(today); setAppliedDateTo(today);
    setSearchInput(''); setSearchTerm('');
    setCurrentPage(1);
  };

  // Export CSV — ดึงทุกแถวตาม filter (ไม่ตัดหน้า) แล้ว gen ฝั่ง client (pattern เดียวกับหน้า Payouts)
  const exportCSV = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      const res = await merchantApi.get(`/merchants/deposit-orders/export?${params.toString()}`);
      const rows: DepositOrder[] = res.data.data || [];
      if (!rows.length) { toast.warning('ไม่มีข้อมูลสำหรับออกรายงาน'); return; }

      const headers = [
        'เลขที่รายการ', 'Reference ID', 'จำนวนเงิน', 'ค่าธรรมเนียม', 'ยอดสุทธิ',
        'สถานะ', 'ช่องทาง', 'ธนาคารปลายทาง', 'เลขบัญชีปลายทาง', 'ชื่อบัญชีปลายทาง',
        'วันที่สร้าง', 'วันที่สำเร็จ',
      ];
      const esc = (v: any) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const fmt = (d?: string) => d ? dayjs(d).tz(TZ).format('DD/MM/YYYY HH:mm:ss') : '';
      const statusLabel = (s: string) => STATUS_MAP[s]?.label || s;

      const csvLines = [
        headers.join(','),
        ...rows.map((d) => [
          esc(d.order_number),
          esc(d.reference_id || ''),
          esc(d.amount),
          esc(d.fee_amount ?? 0),
          esc(d.net_amount ?? d.amount),
          esc(statusLabel(d.status)),
          esc(d.routed_to_payout ? 'P2P' : 'บัญชีเว็บ'),
          esc(d.destination_bank_name || ''),
          esc(d.destination_account_number || ''),
          esc(d.destination_account_name || ''),
          esc(fmt(d.created_at)),
          esc(fmt(d.paid_at)),
        ].join(',')),
      ];
      const blob = new Blob(['﻿' + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `deposits_${appliedDateFrom}_${appliedDateTo}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ออกรายงานไม่สำเร็จ');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const onPaid = () => loadDeposits();
    socketService.on('deposit-paid', onPaid);
    return () => { socketService.off('deposit-paid', onPaid); };
  }, [loadDeposits]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('กรุณาระบุจำนวนเงินที่ถูกต้อง'); return; }
    try {
      setCreating(true);
      const res = await merchantApi.post('/merchants/deposit-orders', { amount: amt });
      if (res.data.success) {
        setCurrent(res.data.data);
        setAmount('');
        toast.success('สร้างรายการฝากสำเร็จ กรุณาโอนเงินตามบัญชีปลายทาง');
        loadDeposits();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'สร้างรายการฝากไม่สำเร็จ');
    } finally {
      setCreating(false);
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !current) return;
    if (!file.type.startsWith('image/')) { toast.error('กรุณาเลือกไฟล์รูปภาพ'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('ไฟล์ใหญ่เกิน 5MB'); return; }
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      try {
        setUploading(true);
        const res = await merchantApi.post(`/merchants/deposit-orders/${current.order_number}/upload-slip`, { imageBase64: base64 });
        // ธนาคาร/provider ดีเลย์ชั่วคราว → backend ตอบ pending (202) ระบบกำลัง retry ให้เอง — ยังไม่จบ (อย่าปิดฟอร์ม)
        // แถวจะกลายเป็น "สำเร็จ" เองผ่าน socket deposit-paid เมื่อ retry สำเร็จ
        if (res.data.pending) {
          toast.info(res.data.message || 'ระบบกำลังตรวจสอบสลิป กรุณารอสักครู่');
          loadDeposits();
        } else if (res.data.success) {
          const nb = res.data.data?.new_balance;
          toast.success(nb != null ? `ยืนยันสำเร็จ! ยอดเงินคงเหลือ ฿${Number(nb).toLocaleString()}` : 'ยืนยันสำเร็จ!');
          setCurrent(null);
          loadDeposits();
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'ตรวจสอบสลิปไม่สำเร็จ');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const copy = (text?: string) => { if (text) { navigator.clipboard.writeText(text); toast.info('คัดลอกแล้ว'); } };

  const secondsLeft = current ? Math.max(0, Math.floor((new Date(current.expires_at).getTime() - Date.now()) / 1000)) : 0;
  const expired = !!current && secondsLeft === 0;
  void tick; // trigger re-render สำหรับเช็ค expired (การแสดงผลใช้ CountdownTimer)

  const depositColumns: Column<DepositOrder>[] = [
    {
      key: 'order_number', header: 'เลขที่',
      render: (d) => (
        <div className="min-w-0">
          <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{d.order_number}</p>
          {d.reference_id && <p className="text-xs text-gray-500 dark:text-slate-400 truncate">Ref: {d.reference_id}</p>}
          <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(d.created_at).format('DD/MM HH:mm')}</p>
        </div>
      ),
    },
    {
      key: 'destination', header: 'บัญชีปลายทาง',
      render: (d) => (
        <div>
          <p className="text-xs text-gray-700 dark:text-slate-200">{d.destination_bank_name} · {d.destination_account_number}</p>
          <span className={`mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${d.routed_to_payout ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'}`}>
            <FontAwesomeIcon icon={d.routed_to_payout ? faArrowRightArrowLeft : faBuilding} className="text-[9px]" />
            {d.routed_to_payout ? 'P2P' : 'บัญชีเว็บ'}
          </span>
        </div>
      ),
    },
    {
      key: 'amount', header: 'จำนวนเงิน', width: '150px',
      render: (d) => (
        <div>
          <p className="font-bold text-sm tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{Number(d.amount).toLocaleString()}</p>
          {(d.fee_amount || 0) > 0 && (
            d.routed_to_payout
              ? <p className="text-[10px] text-gray-400 dark:text-slate-500 tabular-nums">ค่าธรรมเนียม P2P ฿{Number(d.fee_amount).toLocaleString()} (หักจาก fee wallet)</p>
              : <p className="text-[10px] text-gray-400 dark:text-slate-500 tabular-nums">fee ฿{Number(d.fee_amount).toLocaleString()} · net ฿{Number(d.net_amount ?? d.amount).toLocaleString()}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status', header: 'สถานะ', width: '140px',
      render: (d) => <StatusBadge status={d.status} statusMap={STATUS_MAP} />,
    },
    {
      key: 'pay_link', header: 'ลิงก์ชำระ', width: '130px',
      render: (d) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); copy(`${window.location.origin}/pay/${d.order_number}`); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          title="คัดลอกลิงก์หน้าชำระเงินสำหรับส่งให้ลูกค้า"
        >
          <FontAwesomeIcon icon={faLink} /> คัดลอกลิงก์
        </button>
      ),
    },
  ];

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faHandHoldingDollar}
        title="ฝากเงิน (P2P)"
        subtitle="สร้างรายการฝาก ระบบจะจับคู่บัญชีปลายทางให้อัตโนมัติ"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4">
        {/* สร้างรายการฝาก */}
        <form onSubmit={handleCreate} className={`${CARD} p-6 h-fit`}>
          <h2 className="text-base font-bold text-gray-800 dark:text-slate-100 mb-4">สร้างรายการฝาก</h2>
          <UiInput
            label="จำนวนเงินที่ต้องการฝาก"
            required
            prefix="฿"
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="เช่น 300"
          />
          <UiButton type="submit" loading={creating} icon={faPlus} className="w-full justify-center mt-4">
            สร้างรายการ
          </UiButton>
        </form>

        {/* บัญชีปลายทาง + อัปสลิป (หรือ วิธีใช้งาน) */}
        {current ? (
          <div className={`${CARD} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${current.routed_to_payout ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300'}`}>
              <FontAwesomeIcon icon={current.routed_to_payout ? faArrowRightArrowLeft : faBuilding} />
              {current.routed_to_payout ? 'จับคู่ P2P' : 'บัญชีบริษัท/เว็บ'}
            </span>
            <span className={`text-sm font-bold tabular-nums ${expired ? 'text-red-500' : 'text-amber-600 dark:text-amber-400'}`}>
              <FontAwesomeIcon icon={faClock} className="mr-1" />
              {expired ? 'หมดเวลา' : <CountdownTimer expiresAt={current.expires_at} />}
            </span>
          </div>

          <div className="rounded-xl bg-gray-50 dark:bg-slate-700/50 p-4 space-y-3">
            <div className="flex items-center gap-2 text-gray-700 dark:text-slate-200">
              <FontAwesomeIcon icon={faUniversity} style={{ color: 'var(--theme-primary)' }} />
              <span className="font-semibold">{current.destination_bank_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400">เลขบัญชี</p>
                <p className="text-lg font-bold tabular-nums text-gray-800 dark:text-slate-100">{current.destination_account_number}</p>
              </div>
              <button onClick={() => copy(current.destination_account_number)} className="px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-600 border border-gray-200 dark:border-slate-500 text-gray-600 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-500 transition-colors">
                <FontAwesomeIcon icon={faCopy} className="mr-1" /> คัดลอก
              </button>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-slate-400">ชื่อบัญชี</p>
              <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{current.destination_account_name}</p>
            </div>
            <div className="pt-2 border-t border-gray-200 dark:border-slate-600 flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-slate-400">ยอดที่ต้องโอน (เป๊ะ)</span>
              <span className="text-xl font-bold tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{Number(current.amount).toLocaleString()}</span>
            </div>
            {(current.fee_amount || 0) > 0 && (
              current.routed_to_payout ? (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-slate-400">ค่าธรรมเนียม P2P (หักจากกระเป๋า fee)</span>
                  <span className="text-amber-600 dark:text-amber-400 tabular-nums">฿{Number(current.fee_amount).toLocaleString()}</span>
                </div>
              ) : (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-slate-400">ค่าธรรมเนียม · เข้า Wallet</span>
                  <span className="tabular-nums">
                    <span className="text-amber-600 dark:text-amber-400">−฿{Number(current.fee_amount).toLocaleString()}</span>
                    <span className="text-gray-400 dark:text-slate-500"> · </span>
                    <span className="text-green-600 dark:text-green-400 font-semibold">฿{Number(current.net_amount ?? current.amount).toLocaleString()}</span>
                  </span>
                </div>
              )
            )}
          </div>

          <div className="mt-4">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <UiButton
              onClick={() => fileInputRef.current?.click()}
              loading={uploading}
              icon={faCloudUpload}
              className="w-full justify-center"
            >
              {uploading ? 'กำลังตรวจสอบสลิป...' : expired ? 'อัปโหลดสลิปย้อนหลัง' : 'อัปโหลดสลิปเพื่อยืนยัน'}
            </UiButton>
            <p className="text-xs text-center text-gray-400 dark:text-slate-500 mt-2">
              {expired
                ? 'รายการหมดเวลาแล้ว — ถ้าโอนเงินไปแล้วยังอัปสลิปย้อนหลังได้ภายใน 24 ชม.'
                : 'โอนเงินตามยอดและบัญชีด้านบน แล้วอัปสลิปการโอนเพื่อยืนยันอัตโนมัติ'}
            </p>
          </div>
        </div>
        ) : (
          <div className={`${CARD} p-6 h-fit`}>
            <h2 className="text-base font-bold text-gray-800 dark:text-slate-100 mb-4">วิธีฝากเงิน P2P</h2>
            <ol className="space-y-3">
              {['ระบุยอดที่ต้องการฝาก แล้วกด "สร้างรายการ"', 'ระบบจับคู่บัญชีปลายทางอัตโนมัติ — โอนเงินตามยอดและบัญชีที่แสดง', 'แนบสลิปการโอน → ระบบตรวจสอบและยืนยันอัตโนมัติ'].map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: 'var(--theme-primary-gradient)' }}>{i + 1}</span>
                  <span className="text-sm text-gray-600 dark:text-slate-300 pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* รายการล่าสุด — ใช้ DataTable กลาง + server pagination */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-slate-200">รายการฝากล่าสุด</h2>
          <UiButton variant="secondary" icon={exporting ? faRotate : faDownload} onClick={exportCSV} disabled={exporting || deposits.length === 0}>
            {exporting ? 'กำลังออกรายงาน...' : 'Export CSV'}
          </UiButton>
        </div>

        <UiFilterPanel
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onSearch={handleSearch}
          onReset={handleReset}
          loading={loadingList}
        >
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><FontAwesomeIcon icon={faSearch} className="mr-1" />ค้นหา</label>
            <div className="relative">
              <input
                type="text" placeholder="เลขที่รายการฝาก / Reference ID..." value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 pr-7"
              />
              {searchInput && <button type="button" onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"><FontAwesomeIcon icon={faTimes} className="text-xs" /></button>}
            </div>
          </div>
        </UiFilterPanel>

        <DataTable data={deposits} columns={depositColumns} loading={loadingList} serverPagination={serverPaginationProps} />
      </div>
    </div>
  );
};

export default DepositPage;
